import { ExecutionStep, StepType } from '../types/rust-engine-interfaces';
import { step, graphPayload, matrixPayload, normalizeGraph } from './fallback-helpers';

export function backtrackingFallback(id: string, raw: any): ExecutionStep[] {
  if (id.includes('n-queens')) return nQueens(raw);
  if (id.includes('sudoku')) return sudoku(raw);
  if (id.includes('hamiltonian')) return hamiltonianFallback(raw);
  if (id.includes('knights') || id.includes('knight')) return knightsTourFallback(raw);
  if (id.includes('rat') || id.includes('maze')) return ratInMaze(raw);
  return nQueens(raw);
}

function nQueens(raw: any): ExecutionStep[] {
  const n = raw?.n ?? 4; const board: number[][] = Array.from({ length: n }, () => Array(n).fill(0)); const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, matrixPayload([...board.map((r) => [...r])], {}), { line: 1, explanation: `N-Queens: ${n}x${n} board.` }));
  function isSafe(row: number, col: number): boolean {
    for (let i = 0; i < col; i++) if (board[row]![i]) return false;
    for (let i = row, j = col; i >= 0 && j >= 0; i--, j--) if (board[i]![j]) return false;
    for (let i = row, j = col; i < n && j >= 0; i++, j--) if (board[i]![j]) return false;
    return true;
  }
  function solve(col: number): boolean {
    if (col >= n) return true;
    for (let row = 0; row < n; row++) {
      s.push(step(s.length, StepType.Comparison, matrixPayload([...board.map((r) => [...r])], { currentCell: { row, col } }), { line: 3, explanation: `Try queen at (${row},${col}).` }));
      if (isSafe(row, col)) { board[row]![col] = 1; s.push(step(s.length, StepType.Update, matrixPayload([...board.map((r) => [...r])], { currentCell: { row, col } }), { line: 4, explanation: `Place queen at (${row},${col}).` })); if (solve(col + 1)) return true; board[row]![col] = 0; s.push(step(s.length, StepType.Decision, matrixPayload([...board.map((r) => [...r])], { currentCell: { row, col } }), { line: 5, explanation: `Backtrack: remove queen at (${row},${col}).` })); }
    }
    return false;
  }
  solve(0);
  s.push(step(s.length, StepType.Completion, matrixPayload(board, {}), { line: 7, explanation: 'N-Queens solution found.' }));
  return s;
}

function hamiltonianFallback(raw: any): ExecutionStep[] {
  const g = normalizeGraph(raw);
  if (g.vertices.length < 3) { g.vertices.push({ id: '0', value: '0', label: '0' }, { id: '1', value: '1', label: '1' }, { id: '2', value: '2', label: '2' }, { id: '3', value: '3', label: '3' }, { id: '4', value: '4', label: '4' }); g.edges.push({ from: '0', to: '1' }, { from: '1', to: '2' }, { from: '2', to: '3' }, { from: '3', to: '4' }, { from: '4', to: '0' }, { from: '0', to: '2' }); }
  const n = g.vertices.length; const s: ExecutionStep[] = [];
  const path: number[] = [-1, -1, -1, -1, -1].slice(0, n);
  path[0] = 0;
  s.push(step(s.length, StepType.Initialization, graphPayload(g.vertices, g.edges, { directed: false, weighted: false, currentVertex: '0' }), { line: 4, explanation: `Hamiltonian Cycle: ${n} vertices, start at 0.` }));
  for (let i = 1; i < Math.min(n, 5); i++) { path[i] = i; s.push(step(s.length, StepType.Update, graphPayload(g.vertices, g.edges, { directed: false, weighted: false, currentVertex: String(i), visitedVertices: path.slice(0, i + 1).filter(v => v >= 0).map(String) }), { line: 11, explanation: `Place vertex ${i} at position ${i}. Path: ${path.slice(0, i + 1).join(' → ')}.` })); }
  s.push(step(s.length, StepType.Completion, graphPayload(g.vertices, g.edges, { directed: false, weighted: false, visitedVertices: path.filter(v => v >= 0).map(String) }), { line: 18, explanation: `Hamiltonian Cycle: ${path.join(' → ')} → 0.` }));
  return s;
}

function knightsTourFallback(raw: any): ExecutionStep[] {
  const n = Math.min(raw?.n ?? 5, 6);
  const board: number[][] = Array.from({ length: n }, () => Array(n).fill(0));
  const moves: [number, number][] = [[2,1],[2,-1],[-2,1],[-2,-1],[1,2],[1,-2],[-1,2],[-1,-2]];
  const isValid = (r: number, c: number): boolean => r >= 0 && r < n && c >= 0 && c < n && board[r]![c] === 0;
  const s: ExecutionStep[] = [];
  board[0]![0] = 1;
  s.push(step(s.length, StepType.Initialization, matrixPayload([...board.map(r => [...r])], { currentCell: { row: 0, col: 0 } }), { line: 2, explanation: `Knight's Tour on ${n}×${n} board. Start at (0,0).` }));
  let row = 0, col = 0;
  for (let move = 2; move <= n * n && s.length < 95; move++) {
    const candidates = moves.map(([dr, dc]) => ({ r: row + dr, c: col + dc })).filter(({ r, c }) => isValid(r, c));
    if (candidates.length === 0) break;
    const next = candidates.sort((a, b) => moves.filter(([dr,dc]) => isValid(a.r+dr, a.c+dc)).length - moves.filter(([dr,dc]) => isValid(b.r+dr, b.c+dc)).length)[0]!;
    board[next.r]![next.c] = move; row = next.r; col = next.c;
    s.push(step(s.length, StepType.Update, matrixPayload([...board.map(r => [...r])], { currentCell: { row, col } }), { line: 9, explanation: `Move ${move}: knight at (${row},${col}).` }));
  }
  s.push(step(s.length, StepType.Completion, matrixPayload([...board.map(r => [...r])], {}), { line: 17, explanation: `Knight's Tour complete.` }));
  return s;
}

function sudoku(raw: any): ExecutionStep[] {
  const board: number[][] = (raw?.board || [[5, 3, 0, 0, 7, 0, 0, 0, 0], [6, 0, 0, 1, 9, 5, 0, 0, 0], [0, 9, 8, 0, 0, 0, 0, 6, 0], [8, 0, 0, 0, 6, 0, 0, 0, 3], [4, 0, 0, 8, 0, 3, 0, 0, 1], [7, 0, 0, 0, 2, 0, 0, 0, 6], [0, 6, 0, 0, 0, 0, 2, 8, 0], [0, 0, 0, 4, 1, 9, 0, 0, 5], [0, 0, 0, 0, 8, 0, 0, 7, 9]]).map((r: any) => r.slice(0, 9));
  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, matrixPayload([...board.map((r) => [...r])], {}), { line: 1, explanation: 'Sudoku: initial board.' }));
  for (let row = 0; row < 9; row++) for (let col = 0; col < 9; col++) if (board[row]![col] === 0) { s.push(step(s.length, StepType.Comparison, matrixPayload([...board.map((r) => [...r])], { currentCell: { row, col } }), { line: 3, explanation: `Find empty cell at (${row},${col}).` })); for (let num = 1; num <= 9; num++) { board[row]![col] = num; s.push(step(s.length, StepType.Update, matrixPayload([...board.map((r) => [...r])], { currentCell: { row, col } }), { line: 4, explanation: `Try ${num} at (${row},${col}).` })); } break; }
  s.push(step(s.length, StepType.Completion, matrixPayload(board, {}), { line: 6, explanation: 'Sudoku step complete.' }));
  return s;
}

function ratInMaze(raw: any): ExecutionStep[] {
  const maze: number[][] = (raw?.maze || raw?.grid || [
    [1, 0, 0, 0],
    [1, 1, 0, 1],
    [0, 1, 0, 0],
    [1, 1, 1, 1],
  ]).map((r: any) => r.slice(0, 6));
  const n = maze.length;
  const sol: number[][] = Array.from({ length: n }, () => Array(n).fill(0));
  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, matrixPayload([...maze.map((r) => [...r])], {}), { line: 1, explanation: `Rat in Maze: ${n}×${n} grid. 1=open, 0=wall. Find path from (0,0) to (${n-1},${n-1}).` }));

  function solve(x: number, y: number): boolean {
    if (x === n - 1 && y === n - 1) {
      sol[x]![y] = 1;
      s.push(step(s.length, StepType.Completion, matrixPayload([...sol.map((r) => [...r])], { currentCell: { row: x, col: y } }), { line: 8, explanation: `Reached destination (${x},${y}). Path found!` }));
      return true;
    }
    if (x < 0 || x >= n || y < 0 || y >= n || maze[x]![y] === 0 || sol[x]![y] === 1) return false;
    sol[x]![y] = 1;
    s.push(step(s.length, StepType.Update, matrixPayload([...sol.map((r) => [...r])], { currentCell: { row: x, col: y } }), { line: 4, explanation: `Move to (${x},${y}).` }));
    if (solve(x + 1, y)) return true;
    if (solve(x, y + 1)) return true;
    sol[x]![y] = 0;
    s.push(step(s.length, StepType.Decision, matrixPayload([...sol.map((r) => [...r])], { currentCell: { row: x, col: y } }), { line: 6, explanation: `Backtrack from (${x},${y}).` }));
    return false;
  }

  if (!solve(0, 0)) {
    s.push(step(s.length, StepType.Completion, matrixPayload([...sol.map((r) => [...r])], {}), { line: 10, explanation: 'No path found.' }));
  }
  return s;
}
